'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react'

export interface ThemeOption {
  class: string
  color: string
  colorSecondary: string
  color100: string
  color300: string
  color400: string
  borderColor: string
  isCustom: boolean
}

const themeOptions: ThemeOption[] = [
  {
    class: 'bg-primary-1',
    color: '#acc5db',
    colorSecondary: '#8fabc9',
    color100: '#9eb8d2',
    color300: '#7f9dbd',
    color400: '#6f8fb1',
    borderColor: '#4d7394',
    isCustom: false,
  },
  {
    class: 'bg-primary-2',
    color: '#f5ecf4',
    colorSecondary: '#e8c7e6',
    color100: '#f0dcef',
    color300: '#deb2dc',
    color400: '#d49dd2',
    borderColor: '#6e4597',
    isCustom: true,
  },
  {
    class: 'bg-primary-3',
    color: '#deffd2',
    colorSecondary: '#a2c781',
    color100: '#c7eab4',
    color300: '#8ab464',
    color400: '#72a147',
    borderColor: '#01a014',
    isCustom: true,
  },
]

export interface AppContextType {
  themeColor: ThemeOption
  setThemeColor: (theme: ThemeOption) => void
  themeOptions: ThemeOption[]
}

const AppContext = createContext<AppContextType | undefined>(undefined)

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [themeColor, setThemeColor] = useState<ThemeOption>(themeOptions[0])

  const updateThemeColor = useCallback((theme: ThemeOption) => {
    setThemeColor(theme)
    if (typeof window !== 'undefined') {
      if (theme.isCustom) {
        document.documentElement.style.setProperty('--custom-border-color', theme.borderColor)
        document.documentElement.style.setProperty('--custom-background-color', theme.color)
        document.documentElement.style.setProperty('--custom-background-200-color', theme.colorSecondary)
        document.documentElement.style.setProperty('--custom-background-100-color', theme.color100)
        document.documentElement.style.setProperty('--custom-background-300-color', theme.color300)
        document.documentElement.style.setProperty('--custom-background-400-color', theme.color400)
      } else {
        document.documentElement.style.removeProperty('--custom-border-color')
        document.documentElement.style.removeProperty('--custom-background-color')
        document.documentElement.style.removeProperty('--custom-background-200-color')
        document.documentElement.style.removeProperty('--custom-background-100-color')
        document.documentElement.style.removeProperty('--custom-background-300-color')
        document.documentElement.style.removeProperty('--custom-background-400-color')
      }
    }
  }, [])

  return (
    <AppContext.Provider
      value={{
        themeColor,
        setThemeColor: updateThemeColor,
        themeOptions,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export const useAppContext = () => {
  const context = useContext(AppContext)
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider')
  }
  return context
}

export function AppProviderWrapper({
  children,
}: {
  children: React.ReactNode
}) {
  return <AppProvider>{children}</AppProvider>
}
